import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

const TEN_MINUTES = 10 * 60 * 1000;

interface PendingOrgData {
  organizationName: string;
  organizationType?: string;
}

/**
 * Check if there's valid pending org signup data in sessionStorage
 */
export function hasPendingOrgSignup(): boolean {
  const pendingOrgData = localStorage.getItem('pending_org_signup');
  const timestamp = localStorage.getItem('pending_org_signup_timestamp');
  
  if (!pendingOrgData || !timestamp) return false;
  
  const age = Date.now() - parseInt(timestamp);
  return age < TEN_MINUTES;
}

/**
 * Get the pending org signup data if valid
 */
export function getPendingOrgSignup(): PendingOrgData | null {
  if (!hasPendingOrgSignup()) return null;
  
  try {
    const pendingOrgData = localStorage.getItem('pending_org_signup');
    return pendingOrgData ? JSON.parse(pendingOrgData) : null;
  } catch {
    return null;
  }
}

/**
 * Clear the pending org signup data from sessionStorage
 */
export function clearPendingOrgSignup(): void {
  localStorage.removeItem('pending_org_signup');
  localStorage.removeItem('pending_org_signup_timestamp');
}

/**
 * Initialize organization signup for the current user
 * Returns true if successful, false otherwise
 */
export async function initializeOrgSignup(session: Session): Promise<boolean> {
  const pendingData = getPendingOrgSignup();
  
  if (!pendingData) {
    console.warn('[OrgSignup] No valid pending org data found');
    return false;
  }
  
  const orgInitData = {
    name: pendingData.organizationName,
    type: pendingData.organizationType || 'school'
  };
  
  console.debug('[OrgSignup] Initializing org account:', orgInitData);
  
  try {
    const { error } = await supabase.rpc('init_account_for_current_user', {
      p_role: 'organization_admin',
      p_org: orgInitData
    });
    
    if (error && !error.message?.includes('duplicate')) {
      console.error('[OrgSignup] init_account_for_current_user failed:', error);
      return false;
    }
    
    // Set default consents
    await supabase.rpc('set_user_consent', { p_type: 'terms_of_service', p_given: true });
    await supabase.rpc('set_user_consent', { p_type: 'privacy_policy', p_given: true });
    
    // Clear sessionStorage after successful init
    clearPendingOrgSignup();
    
    console.debug('[OrgSignup] Organization signup completed successfully');
    return true;
  } catch (err) {
    console.error('[OrgSignup] Exception during org initialization:', err);
    return false;
  }
}
